import clsx from 'clsx';
import { PrimaryButton } from 'Components/Buttons';
import SocialLoginBar from 'Components/SocialLoginBar';
import { CheckBox } from 'grommet';
import { ComponentProps, ReactNode } from 'preact/compat';
import { useEffect, useState } from 'preact/hooks';
import { useDispatch } from 'react-redux';
import { buildURL, post } from 'Shared/fetch';
import { logPageViewGA } from 'Shared/ga';
import { trackMP } from 'Shared/mp';

import type { Persona } from '@/components/SpeedyFlow/types';
import { emailRegex, getCampaignData, getDeviceType } from '@/helpers';
import checkLocalStorageForODTP from '@/helpers/checkLocalStorageForODTP';
import getDeviceId from '@/helpers/getDeviceId';
import { setUserData } from '@/redux/slices/user/actions';
import getEmailVerificationPath from '@/routes/emailVerify/Verification/getEmailVerificationPath';
import style from '@/routes/login/style.scss';

const TERMS_VERSION = process.env.PREACT_APP_TERMS_VERSION ?? 1;

export interface SignupResult {
  user: Record<string, any>;
  planData?: { plan?: string; price?: string };
  userAlreadyExist?: boolean;
  /** The trimmed, lowercased address that was registered. */
  email: string;
  /** Query string carrying plan/price/email, for the email-verification destination. */
  emailVerificationSearch: string;
}

interface SignupFormProps {
  plan?: string;
  price?: string;
  /** Raw query-param value, passed through unchanged: it reaches the API in `referralData`. */
  isGiftCardUser?: boolean | string;
  /** Offline day ticket purchase token from the URL. */
  odtp?: string | null;
  submitLabel?: string;
  /** An error surfaced by the caller, e.g. a failed social login round trip. */
  initialEmailError?: string;
  /** Rendered under the submit button — a link on the page, a step switch in the modal. */
  footer?: ReactNode;
  /**
   * Where the emailed verification link should land, when the caller knows better than the saved
   * post-auth destination does.
   *
   * The Speedy Flow does: it was opened by a gate on a particular page, and that page is where the
   * visitor should come back to. The saved destination is the last *other* page they happened to
   * visit, which survives a move to `/home` on purpose — so without this, signing up in a modal on
   * `/home` sent the link to a habitat they had browsed earlier and left.
   */
  emailVerificationPath?: string;
  /**
   * What the visitor said brings them here, when they were asked before the account existed.
   *
   * Sent with the registration rather than patched on afterwards: the account is then never briefly
   * one without an answer, and it needs no second request on a step the visitor is watching.
   */
  persona?: Persona;
  /** Called once the account exists. The caller decides where to go next. */
  onSignedUp: (result: SignupResult) => void;
}

/**
 * The email signup form, shared by the `/signup` page and the Speedy Flow's sign-up step.
 *
 * Everything that is the same in both lives here: validation, the POST, the analytics that fire on
 * success, and the terms gate. Everything that differs — where the user goes afterwards, the page
 * chrome, the heading — is the caller's, which is why this ends at `onSignedUp` rather than
 * navigating.
 *
 * The age gate is a single checkbox rather than an age-group select: the visitor confirms they are
 * 13 or older and accepts the terms in one affirmative action. It starts unchecked, because
 * pre-ticked consent is not consent.
 */
const SignupForm = ({
  plan,
  price,
  isGiftCardUser,
  odtp = null,
  submitLabel,
  initialEmailError = '',
  footer,
  emailVerificationPath,
  persona,
  onSignedUp,
}: SignupFormProps) => {
  const dispatch = useDispatch();
  const deviceId = getDeviceId();

  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState(initialEmailError);
  const [passwordRequired, setPasswordRequired] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [serverError, setServerError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isTermsAccepted, setIsTermsAccepted] = useState(false);
  const [termsError, setTermsError] = useState('');

  /**
   * The caller can only learn about some errors after this form is on screen — a failed social
   * login resolves in an effect of theirs, which by definition runs after ours. Reading the prop in
   * the `useState` initialiser alone dropped exactly those: arriving from the social callback, the
   * page is already past its session check, so this mounts in the same commit and the message that
   * lands a tick later was never shown.
   *
   * Only ever sets. Clearing on an empty prop would wipe an error the visitor has just been given
   * by their own submission.
   */
  useEffect(() => {
    if (initialEmailError) {
      setEmailError(initialEmailError);
    }
  }, [initialEmailError]);

  const onEmailChange: ComponentProps<'input'>['onChange'] = ({ currentTarget }) => {
    setEmail(currentTarget.value);
    if (emailError) {
      setEmailError(!currentTarget.value.trim().match(emailRegex) ? emailError : '');
    }
  };

  const onSubmit: ComponentProps<'form'>['onSubmit'] = async (evt) => {
    evt.preventDefault();
    const formData = new FormData(evt.currentTarget);
    const password = formData.get('password') as string;

    setEmailError('');
    setTermsError('');

    const trimmedEmail = email.trim();
    const odtpParam = odtp ? checkLocalStorageForODTP(odtp) : null;

    if (!trimmedEmail) {
      setEmailError('Email cannot be empty');
      return;
    }
    if (!trimmedEmail.match(emailRegex)) {
      setEmailError('Please enter a valid email. ');
      return;
    }
    if (!isTermsAccepted) {
      setTermsError("Please confirm you are 13 or older and agree to zoolife's Terms of usage & Privacy policy.");
      return;
    }
    // Admin-only path: the server asks for a password on a second attempt.
    if (passwordRequired && !password) {
      setPasswordError('Password cannot be empty');
      return;
    }

    setPasswordRequired(false);
    setPasswordError('');

    try {
      setIsLoading(true);
      const { userAgent } = navigator;
      const {
        user,
        planData,
        userAlreadyExist,
        emailError: emailErrorFromServer,
        passwordError: passwordErrorFromServer,
        error,
        passwordRequired: passwordRequiredFromServer,
      } = await post(buildURL('/users/signup'), {
        email: trimmedEmail.toLowerCase(),
        origin: getDeviceType(),
        termsVersion: TERMS_VERSION,
        platform: 'webApp',
        password,
        referralData: { ...getCampaignData(), userAgent, isGiftCardUser },
        planData: { plan, price },
        userDisabledAt: localStorage.getItem('userDisabledAt'),
        emailVerificationPath: emailVerificationPath ?? getEmailVerificationPath({ isGiftCardUser, plan, price }),
        odtp: odtpParam,
        deviceId,
        persona,
      });

      if (emailErrorFromServer) {
        setEmailError(error);
        setIsLoading(false);
        return;
      }
      if (passwordErrorFromServer) {
        setPasswordError(error);
        setPasswordRequired(passwordRequiredFromServer ?? false);
        setIsLoading(false);
        return;
      }
      if (error) {
        setServerError(error);
        setIsLoading(false);
        return;
      }
      if (!user) {
        // No user and no error to explain why. Nothing above has cleared the spinner, so without
        // this the button stays loading and the visitor cannot even retry.
        setServerError('Something went wrong, please try again.');
        setIsLoading(false);
        return;
      }

      if (!userAlreadyExist) {
        logPageViewGA('/signed-up', false, false);
      } else {
        trackMP('login', { method: 'login-page' });
      }
      if (typeof window !== 'undefined' && window.fbq) {
        window.fbq('trackCustom', 'SignedUp');
      }
      setServerError('');

      try {
        localStorage.setItem('returningUser', 'true');
      } catch (err) {
        console.warn('Local storage is not available.');
        console.error(err);
      }

      const params: Record<string, any> = {};
      if (planData?.plan && planData?.price) {
        params.plan = planData.plan;
        params.price = planData.price;
      }
      if (isGiftCardUser) {
        params.isGiftCardUser = true;
      }
      params.email = trimmedEmail;

      dispatch(setUserData(user));
      onSignedUp({
        user,
        planData,
        userAlreadyExist,
        email: trimmedEmail,
        emailVerificationSearch: new URLSearchParams(params).toString(),
      });
    } catch (err) {
      console.error(err);
      setServerError('Something went wrong, please try again.');
      setIsLoading(false);
    }
  };

  return (
    <>
      <SocialLoginBar />
      <div className="mt-4 flex flex-col items-center">
        <p className="preflight preflight-p text-center text-xs text-grey-3">
          I confirm I am over 13, and agree to zoolife&apos;s{' '}
          <a
            data-native
            href="/terms-and-conditions"
            target="_blank"
            className="preflight preflight-a underline hover:text-grey-2"
          >
            Terms of usage and Privacy policy
          </a>
        </p>
      </div>

      <div className="relative mx-5 my-6 flex items-center justify-center">
        <div className="absolute inset-x-0 top-1/2 h-px bg-[#dadada]" />
        <span className="relative block bg-white px-4 text-xs/6 text-zinc-500">OR</span>
      </div>

      <form onSubmit={onSubmit} className={style.form}>
        <div className="flex w-full grow flex-col items-center justify-center">
          <div className={style.inputContainer}>
            <div className={style.inputWrapper}>
              <input
                name="email"
                type="email"
                placeholder="Email"
                autoComplete="email"
                value={email}
                onChange={onEmailChange}
                className={clsx(emailError && style.errorBorder)}
              />
            </div>
            <div className={clsx(style.errorSection, emailError && style.active)}>{emailError}</div>
          </div>
          {passwordRequired && (
            <div className={style.inputContainer}>
              <div className={style.inputWrapper}>
                <input
                  name="password"
                  type="password"
                  placeholder="Password"
                  autoComplete="new-password"
                  className={clsx(passwordError && style.errorBorder)}
                />
              </div>
              <div className={clsx(style.errorSection, passwordError && style.active)}>{passwordError}</div>
            </div>
          )}
        </div>

        <div className="flex shrink-0 flex-col items-center justify-center">
          {serverError && (
            <div className="my-4 flex justify-center">
              <div className={clsx(style.errorSection, style.active, '!w-auto')}>{serverError}</div>
            </div>
          )}
          <div className={clsx('mt-4 flex flex-col items-center justify-center', termsError && 'error')}>
            <CheckBox
              label={
                <span className="text-sm text-grey-3">
                  I am 13 or older, and agree to zoolife&apos;s{' '}
                  <a
                    data-native
                    href="/terms-and-conditions"
                    target="_blank"
                    className="preflight preflight-a underline hover:text-grey-2"
                  >
                    Terms of usage &amp; Privacy policy
                  </a>
                </span>
              }
              checked={isTermsAccepted}
              onChange={(event) => setIsTermsAccepted(event.currentTarget.checked)}
              className={clsx(termsError && style.errorBorder)}
            />
            <div className={clsx(style.errorSection, termsError && style.active)}>{termsError}</div>
          </div>
          <PrimaryButton
            type="submit"
            label={submitLabel ?? 'Join'}
            className="mx-auto mb-4 w-full max-w-[416px]"
            loading={isLoading}
          />
          {footer}
        </div>
      </form>
    </>
  );
};

export default SignupForm;
